import {
  QueryClient,
  QueryClientProvider as RQueryClientProvider
} from '@tanstack/react-query';
import { PropsWithChildren, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Helper to check if error is a server error (500) or auth error (401)
const shouldHandleError = (error: any) => {
  // Only handle errors from our API that have the specific shape we expect
  if (!error?.status || typeof error.status !== 'number') return false;

  // Only handle 500 and 401 errors
  return error.status === 500 || error.status === 401;
};

// List of public routes that shouldn't redirect on 401
const PUBLIC_ROUTES = ['/privacy', '/terms', '/auth'];

export const QueryClientProvider = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate();
  const location = useLocation();

  const isPublicRoute = PUBLIC_ROUTES.includes(location.pathname);

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000,
            retry: (failureCount, error: any) => {
              if (error?.status === 401) return false;
              if (error?.status === 500) return failureCount < 3;
              return failureCount < 2;
            },
            retryDelay: (attemptIndex) =>
              Math.min(1000 * 2 ** attemptIndex, 30000),
          },
        },
      }),
  );

  // Set up global error handling
  queryClient.getQueryCache().config.onError = (error: any) => {
    if (!shouldHandleError(error)) return;

    if (error.status === 401 && !isPublicRoute) {
      navigate('/auth');
    } else if (error.status === 502) {
      navigate('/500');
    }
  };

  queryClient.getMutationCache().config.onError = (error: any) => {
    if (!shouldHandleError(error)) return;

    if (error.status === 401 && !isPublicRoute) {
      navigate('/auth');
    } else if (error.status === 502) {
      navigate('/500');
    }
  };

  return (
    <RQueryClientProvider client={queryClient}>{children}</RQueryClientProvider>
  );
};
